/**
 * /my-favourites — signed-in shopper's saved property bookmarks.
 * Also works for anonymous visitors (reads from localStorage) so the page
 * doesn't feel broken before login, and prompts a sign-in to persist them.
 */
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, ArrowLeft, LogIn, Trash2, GitCompare } from "lucide-react";

import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { useFavourites } from "@/hooks/useFavourites";
import { useBackHref } from "@/hooks/useBackHref";
import { resolveImageUrl } from "@/utils/imageUrl";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

function money(price, cur = "£") {
  if (!price && price !== 0) return "POA";
  try { return `${cur}${Number(price).toLocaleString()}`; } catch { return `${cur}${price}`; }
}

export default function MyFavouritesPage() {
  const { shopper, signIn } = useShopperAuth();
  const { items, loading, toggle } = useFavourites();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(() => new Set());
  // If the visitor is signed in as a property-admin agent, track the ids of
  // properties they own so we can flag them as "Your listing" in the grid.
  const [mineIds, setMineIds] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-admin/me`, { withCredentials: true });
        if (cancelled) return;
        const linked = data?.linked_properties || [];
        setMineIds(new Set(linked.map((p) => p.property_id)));
      } catch {
        // Not signed in as an agent — nothing to flag.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const MAX_COMPARE = 3;
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_COMPARE) next.add(id);
      return next;
    });
  };
  const startCompare = () => {
    if (selected.size < 2) return;
    navigate(`/compare?ids=${Array.from(selected).join(",")}`, {
      state: { from: "/my-favourites" },
    });
  };
  const canCompare = selected.size >= 2 && selected.size <= MAX_COMPARE;
  const back = useBackHref("/properties");

  return (
    <PageShell chrome={true}>
      <div data-public-site="true" className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <Link to={back.href} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="favourites-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
          </Link>
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-serif mb-1 flex items-center gap-2">
                <Heart className="h-6 w-6 fill-red-500 text-red-500" />
                My Favourites
              </h1>
              <p className="text-sm text-muted-foreground">
                {shopper
                  ? "Your bookmarked properties, saved to your account."
                  : "You're browsing anonymously — favourites are stored on this device. Sign in to sync them across devices."}
              </p>
            </div>
            {!shopper && (
              <Button onClick={() => signIn("/my-favourites")} data-testid="favourites-sign-in-btn">
                <LogIn className="h-4 w-4 mr-2" /> Sign in
              </Button>
            )}
          </div>

          {items.length > 1 && (
            <div
              className="mb-4 flex items-center justify-between border border-border rounded-lg bg-muted/30 p-3"
              data-testid="favourites-compare-bar"
            >
              <div className="text-sm">
                <span className="font-medium">{selected.size}</span> of up to {MAX_COMPARE} selected
                <span className="text-muted-foreground"> — tick 2 or 3 properties to compare them side by side</span>
              </div>
              <div className="flex items-center gap-2">
                {selected.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected(new Set())}
                    data-testid="favourites-compare-clear"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  onClick={startCompare}
                  disabled={!canCompare}
                  data-testid="favourites-compare-btn"
                >
                  <GitCompare className="h-4 w-4 mr-2" />
                  Compare {canCompare ? `(${selected.size})` : ""}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-16">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center border border-dashed border-border rounded-lg p-12" data-testid="favourites-empty">
              <Heart className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">You haven&apos;t bookmarked any properties yet.</p>
              <Link to="/properties" className="text-primary hover:underline text-sm font-medium">
                Browse properties →
              </Link>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="favourites-list">
              {items.map((f) => {
                const p = f.property || {};
                const img = (p.gallery || [])[0]?.media_url || "";
                const isSelected = selected.has(f.property_id);
                const disabled = !isSelected && selected.size >= MAX_COMPARE;
                const mine = mineIds.has(f.property_id);
                return (
                  <li
                    key={f.property_id}
                    className={`border rounded-lg overflow-hidden bg-background hover:shadow-md transition-shadow relative ${isSelected ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
                    data-testid={`favourite-card-${f.property_id}`}
                  >
                    {/* Compare-select checkbox — sits over the image. Disabled
                        once MAX_COMPARE properties are picked so the user
                        can't overshoot. */}
                    <label
                      className={`absolute top-2 left-2 z-10 flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-md shadow text-xs font-medium cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`favourite-compare-select-${f.property_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => !disabled && toggleSelect(f.property_id)}
                        className="h-3.5 w-3.5"
                      />
                      Compare
                    </label>
                    {mine && (
                      <span
                        className="absolute top-2 right-2 z-10 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded bg-primary text-primary-foreground shadow"
                        data-testid={`favourite-mine-${f.property_id}`}
                      >
                        Your listing
                      </span>
                    )}
                    <Link to={`/property/${f.property_id}`}>
                      <div
                        className="h-40 bg-cover bg-center bg-muted"
                        style={img ? { backgroundImage: `url(${resolveImageUrl(img)})` } : {}}
                      />
                    </Link>
                    <div className="p-4">
                      <Link to={`/property/${f.property_id}`} className="font-medium hover:text-primary">
                        {p.title || "Property"}
                      </Link>
                      <div className="text-sm text-muted-foreground">{p.display_address || ""}</div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="font-semibold">{money(p.price, p.currency || "£")}</div>
                        <button
                          type="button"
                          onClick={() => toggle(f.property_id)}
                          className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                          aria-label="Remove"
                          data-testid={`favourite-remove-${f.property_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </PageShell>
  );
}
